import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useLocation } from "react-router-dom";
import { SxProps, Theme } from "@mui/material/styles";
import { HeaderProps } from "types";
import { useText } from "context/TextProvider";
import { useAppNavigate } from "hooks/useAppNavigate";

const DEFAULT_LOGO_SRC = "";
const DEFAULT_HOME_PATH = "/";
const DEVICE_GRANT_PATH_PREFIX = "/device-grant-flow";
const DEVICE_GRANT_VALIDATE_USER_CODE_PATH = "/device-grant-flow/validate-user-code";

const CONTAINER_SX: SxProps<Theme> = {
  py: "2.5rem",
  display: "flex",
  justifyContent: "center",
};

const LOGO_WRAPPER_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
};

const Header = ({
  logoSrc = DEFAULT_LOGO_SRC,
  logoAlt,
  onLogoClick,
  title,
  subtitle,
  sx = CONTAINER_SX,
}: HeaderProps): JSX.Element => {
  const text = useText();
  const resolvedLogoAlt = logoAlt ?? text.header.logoAlt;
  const appNavigate = useAppNavigate();
  const location = useLocation();

  // Generic default: clicking the logo goes home, preserving config_id —
  // except within the device grant flow, where "home" is the validate-code
  // page since there's no standalone home page in that flow.
  // Any caller can still override this via onLogoClick.
  const handleLogoClick =
    onLogoClick ??
    (() =>
      appNavigate(
        location.pathname.startsWith(DEVICE_GRANT_PATH_PREFIX)
          ? DEVICE_GRANT_VALIDATE_USER_CODE_PATH
          : DEFAULT_HOME_PATH
      ));

  return (
    <Box sx={sx}>
      <Box sx={LOGO_WRAPPER_SX} onClick={handleLogoClick}>
        <Box component="img" src={logoSrc} alt={resolvedLogoAlt} sx={{ maxWidth: "11.5rem" }} />
        {title && (
          <Typography component="h1" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography component="h2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Header;
